import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { getPosts } from '../services/blogService';
import { formatDateTime } from '../utils/formatters';
import { useSettings } from '../context/SettingsContext';

const SinglePost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    const fetchPost = async () => {
      const posts = await getPosts();
      // Try finding by slug first, fallback to id for older posts
      const foundPost = posts.find(p => p.slug === slug || p.id === slug);
      if (foundPost) {
        setPost(foundPost);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return <div className="loading-container"><p>جاري التحميل...</p></div>;
  }

  if (!post) {
    return (
      <div className="container error-container fade-in">
        <div className="error-card">
          <h2>المقال غير موجود</h2>
          <p className="error-message">عذراً، المقال الذي تبحث عنه غير موجود أو تم حذفه.</p>
          <button onClick={() => navigate('/blog')} className="error-retry-btn">
            العودة للمدونة
          </button>
        </div>
      </div>
    );
  }

  // Clean HTML to prevent XSS attacks while keeping valid formatting
  const sanitizedContent = DOMPurify.sanitize(post.content);

  // Strip HTML for default meta description fallback
  const tmp = document.createElement("DIV");
  tmp.innerHTML = post.content;
  const plainText = tmp.textContent || tmp.innerText || "";
  const defaultMetaDescription = plainText.substring(0, 150) + "...";

  const siteTitle = settings?.siteTitle || 'أسعار الذهب';
  const finalSeoTitle = post.seoTitle && post.seoTitle.trim() !== '' ? post.seoTitle : `${post.title} | ${siteTitle}`;
  const finalSeoDescription = post.seoDescription && post.seoDescription.trim() !== '' ? post.seoDescription : defaultMetaDescription;

  return (
    <article className="single-post-page fade-in">
      <Helmet>
        <title>{finalSeoTitle}</title>
        <meta name="description" content={finalSeoDescription} />
        {/* Canonical Link is good practice for SEO */}
        <link rel="canonical" href={`https://bekamdahab.com/post/${post.slug || post.id}`} />
        
        {/* Structured Data for Blog Post */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "image": [post.coverImage || "https://bekamdahab.com/fav.png"],
            "datePublished": new Date(post.date).toISOString(),
            "author": [{
              "@type": "Person",
              "name": post.author || "بكام الذهب"
            }]
          })}
        </script>
      </Helmet>

      {post.coverImage && (
        <div className="post-hero-image" style={{ backgroundImage: `url(${post.coverImage})` }}>
          <div className="post-hero-overlay">
            <div className="container">
              {post.category && <span className="post-category-badge">{post.category}</span>}
              <h1 className="post-main-title">{post.title}</h1>
              <div className="post-meta-info">
                <span>{formatDateTime(post.date)}</span>
                <span className="post-author-badge">بواسطة: {post.author || 'أسعار الذهب'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container post-content-container">
        {!post.coverImage && (
          <header className="post-text-header">
            {post.category && <span className="post-category-badge">{post.category}</span>}
            <h1 className="post-main-title">{post.title}</h1>
            <div className="post-meta-info">
              <span>{formatDateTime(post.date)}</span>
              <span className="post-author-badge">بواسطة: {post.author || 'أسعار الذهب'}</span>
            </div>
          </header>
        )}

        <div 
          className="post-html-content ql-editor" 
          dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
        />

        <div className="post-footer">
          <Link to="/blog" className="back-to-blog-btn">
            &rarr; العودة للمدونة
          </Link>
        </div>
      </div>
    </article>
  );
};

export default SinglePost;
